import { BarChart3, CalendarDays, TrendingUp, Loader2, AlertTriangle, Users, Phone, MessageCircle, Mail, Linkedin, type LucideIcon } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { format } from 'date-fns';

interface UsageLimitsSettingsProps {
  onSectionChange?: (section: string) => void;
}

export function UsageLimitsSettings({ onSectionChange }: UsageLimitsSettingsProps) {
  const { usageData, billingHistory, isLoading, error } = useSubscription();

  if (isLoading) {
    return (
      <div className="max-w-6xl flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#1875FF]" />
      </div>
    );
  }

  if (error || !usageData?.has_subscription) {
    return (
      <div className="max-w-6xl">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-900">No Active Subscription</h3>
            <p className="text-sm text-amber-700 mt-1">
              {error || 'Usage tracking requires an active subscription.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { plan, billing_period, usage, overage } = usageData;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);
  };

  const periodStartFormatted = format(new Date(billing_period.period_start), 'MMMM d, yyyy');
  const periodEndFormatted = format(new Date(billing_period.period_end), 'MMMM d, yyyy');

  const usageCategories: { category: string; icon: LucideIcon; current: number; limit: number; unit: string; overageUnits: number; overageCost: number; pricePerUnit: number }[] = [
    { category: 'Processed Leads', icon: Users, current: usage.processed_leads, limit: plan.leads_included, unit: 'leads', overageUnits: overage.leads.over_by, overageCost: overage.leads.cost, pricePerUnit: plan.overage_price_per_lead },
    { category: 'Calls Done', icon: Phone, current: usage.calls, limit: plan.calls_included, unit: 'calls', overageUnits: overage.calls.over_by, overageCost: overage.calls.cost, pricePerUnit: plan.overage_price_per_call },
    { category: 'WhatsApp Messages Sent', icon: MessageCircle, current: usage.whatsapp, limit: plan.whatsapp_included, unit: 'messages', overageUnits: overage.whatsapp.over_by, overageCost: overage.whatsapp.cost, pricePerUnit: plan.overage_price_per_whatsapp },
    { category: 'Emails Sent', icon: Mail, current: usage.emails, limit: plan.emails_included, unit: 'emails', overageUnits: overage.emails.over_by, overageCost: overage.emails.cost, pricePerUnit: plan.overage_price_per_email },
    { category: 'LinkedIn Actions Done', icon: Linkedin, current: usage.linkedin, limit: plan.linkedin_included, unit: 'actions', overageUnits: overage.linkedin.over_by, overageCost: overage.linkedin.cost, pricePerUnit: plan.overage_price_per_linkedin },
  ];

  const hasAnyOverage = overage.total_overage_cost > 0;
  const overageCategories = usageCategories.filter(c => c.overageUnits > 0);

  return (
    <div className="max-w-6xl space-y-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
          <BarChart3 className="w-6 h-6 text-[#1875FF]" />
        </div>
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Usage & Limits</h2>
          <p className="text-gray-600">Monitor your current usage and plan limits</p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-lg flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-[#1875FF]" />
          </div>
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Current Billing Period</h3>
            <p className="text-sm text-gray-600">{periodStartFormatted} - {periodEndFormatted}</p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-sm text-gray-500">{billing_period.days_remaining} days remaining</div>
          </div>
        </div>

        <div className="space-y-6">
          {usageCategories.map((item) => {
            const rawPercentage = item.limit > 0 ? (item.current / item.limit) * 100 : 0;
            const barPercentage = Math.min(rawPercentage, 100);
            const isOverLimit = item.current > item.limit;
            const isNearLimit = rawPercentage >= 80 && !isOverLimit;
            const overagePercentage = isOverLimit ? Math.min(((item.current - item.limit) / item.limit) * 100, 100) : 0;

            return (
              <div key={item.category} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                      <item.icon className="w-3.5 h-3.5 text-[#1875FF]" />
                      {item.category}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      {item.current.toLocaleString()} of {item.limit.toLocaleString()} {item.unit}
                      {isOverLimit && (
                        <span className="text-amber-600 font-medium ml-1">
                          (+{item.overageUnits.toLocaleString()} overage)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOverLimit ? (
                      <span className="text-sm font-bold text-amber-600">
                        {rawPercentage.toFixed(0)}%
                      </span>
                    ) : (
                      <span
                        className={`text-sm font-bold ${
                          isNearLimit
                            ? 'bg-gradient-to-r from-[#1875FF] to-[#05E0D1] bg-clip-text text-transparent'
                            : 'text-gray-900'
                        }`}
                      >
                        {rawPercentage.toFixed(0)}%
                      </span>
                    )}
                    {(isNearLimit || isOverLimit) && (
                      <TrendingUp className={`w-4 h-4 ${isOverLimit ? 'text-amber-600' : 'text-[#1875FF]'}`} />
                    )}
                  </div>
                </div>
                <div className="relative w-full bg-[#E9FBFF] rounded-full h-3 overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${barPercentage}%`,
                      background: isOverLimit
                        ? 'linear-gradient(90deg, #1875FF 0%, #05E0D1 70%, #F59E0B 100%)'
                        : 'linear-gradient(90deg, #1875FF 0%, #05E0D1 100%)'
                    }}
                  />
                  {isOverLimit && (
                    <div
                      className="absolute top-0 right-0 h-full rounded-r-full transition-all duration-700 ease-out bg-amber-400/40"
                      style={{ width: `${Math.min(overagePercentage, 30)}%` }}
                    />
                  )}
                </div>
                {isOverLimit && (
                  <p className="text-xs font-medium text-amber-600">
                    {item.overageUnits.toLocaleString()} {item.unit} over limit -- {formatCurrency(item.overageCost)} overage charge ({formatCurrency(item.pricePerUnit)}/{item.unit.slice(0, -1) || item.unit})
                  </p>
                )}
                {isNearLimit && (
                  <p className="text-xs font-medium bg-gradient-to-r from-[#1875FF] to-[#05E0D1] bg-clip-text text-transparent">
                    You're approaching your limit. Consider upgrading your plan.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {hasAnyOverage && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Overage Charges</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Category</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Included</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Used</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Overage Units</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Price/Unit</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Overage Cost</th>
                </tr>
              </thead>
              <tbody>
                {overageCategories.map((item) => (
                  <tr key={item.category} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm text-gray-900 font-medium">{item.category}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{item.limit.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{item.current.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-amber-700 font-medium text-right">+{item.overageUnits.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{formatCurrency(item.pricePerUnit)}</td>
                    <td className="py-3 px-4 text-sm text-amber-700 font-semibold text-right">{formatCurrency(item.overageCost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200">
                  <td colSpan={5} className="py-3 px-4 text-sm font-semibold text-gray-900 text-right">Total Overage</td>
                  <td className="py-3 px-4 text-sm font-bold text-amber-700 text-right">{formatCurrency(overage.total_overage_cost)}</td>
                </tr>
                <tr>
                  <td colSpan={5} className="py-2 px-4 text-sm font-semibold text-gray-900 text-right">Estimated Total This Period</td>
                  <td className="py-2 px-4 text-sm font-bold text-gray-900 text-right">{formatCurrency(usageData.estimated_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl border border-[#1875FF]/20 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display mb-2">Need More Resources?</h3>
            <p className="text-sm text-gray-700">
              Upgrade your plan to increase limits and unlock additional features
            </p>
          </div>
          <button
            onClick={() => {
              onSectionChange?.('subscription');
              setTimeout(() => {
                document.getElementById('choose-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 300);
            }}
            className="px-4 py-2 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all"
          >
            Upgrade Plan
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <h3 className="text-lg font-extrabold text-gray-900 font-display mb-4">Usage History</h3>
        {billingHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Period</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Plan</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Leads</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Calls</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">WhatsApp</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Emails</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">LinkedIn</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Base</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Overage</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm text-gray-900">
                      {format(new Date(row.period_start), 'MMM d')} - {format(new Date(row.period_end), 'MMM d, yyyy')}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">{row.plan_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{row.processed_leads_count.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{row.calls_count.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{row.whatsapp_count.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{row.emails_count.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{row.linkedin_count.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 text-right">{formatCurrency(row.base_charge)}</td>
                    <td className="py-3 px-4 text-sm text-right">
                      {row.overage_charge > 0 ? (
                        <span className="text-amber-700 font-medium">{formatCurrency(row.overage_charge)}</span>
                      ) : (
                        <span className="text-gray-400">--</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm font-semibold text-gray-900 text-right">{formatCurrency(row.total_charge)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No previous billing periods yet. Usage history will appear here after your first billing cycle completes.</p>
        )}
      </div>
    </div>
  );
}
